import { Instagram, Facebook, Linkedin } from 'lucide-react';
import { formatDateTime24 } from '../utils/formatTime24';

export const RED = {
  instagram: {
    nombre: 'Instagram',
    Icono: Instagram,
    color: '#d6249f',
    fondo: 'bg-gradient-to-br from-amber-400 via-pink-500 to-purple-600',
    chip: 'bg-pink-50 text-pink-700 border-pink-200',
    queEs: 'Publicá fotos, carruseles, reels e historias en tu Instagram.',
    requisitos: [
      'Tu Instagram tiene que ser cuenta profesional (empresa o creador). Es gratis y se cambia en un minuto.',
      'Cómo se cambia: en la app de Instagram, tu perfil › menú ☰ › Configuración › Tipo de cuenta y herramientas › Cambiar a cuenta profesional.',
      'No hace falta tener página de Facebook.',
    ],
  },
  facebook: {
    nombre: 'Facebook',
    Icono: Facebook,
    color: '#1877f2',
    fondo: 'bg-[#1877f2]',
    chip: 'bg-blue-50 text-blue-700 border-blue-200',
    queEs: 'Publicá en la página de Facebook de tu empresa.',
    requisitos: [
      'Tenés que ser administrador de la página de la empresa (no se publica en perfiles personales).',
      'En la pantalla de Facebook elegí la página o las páginas donde querés publicar.',
    ],
  },
  linkedin: {
    nombre: 'LinkedIn',
    Icono: Linkedin,
    color: '#0a66c2',
    fondo: 'bg-[#0a66c2]',
    chip: 'bg-sky-50 text-sky-700 border-sky-200',
    queEs: 'Publicá en tu perfil de LinkedIn.',
    requisitos: [
      'Entrás con tu usuario de LinkedIn y autorizás a LisualPro a publicar por vos.',
      'LinkedIn pide volver a conectar la cuenta cada 60 días: te avisamos antes.',
    ],
  },
};

export const ESTADO = {
  programada: { t: 'Programada', c: 'bg-blue-50 text-blue-700' },
  publicando: { t: 'Publicando…', c: 'bg-amber-50 text-amber-700' },
  publicada: { t: 'Publicada', c: 'bg-emerald-50 text-emerald-700' },
  parcial: { t: 'Salió en algunas', c: 'bg-orange-50 text-orange-700' },
  error: { t: 'No salió', c: 'bg-red-50 text-red-700' },
  cancelada: { t: 'Cancelada', c: 'bg-gray-100 text-gray-500' },
};

export const TZ = 'America/Argentina/Buenos_Aires';
/** "AAAA-MM-DD" y "HH:MM" en hora de Argentina -> ISO con -03:00 */
export const isoAR = (fecha, hora) => `${fecha}T${hora || '09:00'}:00-03:00`;
export const partesAR = (iso) => {
  const d = new Date(new Date(iso).getTime() - 3 * 3600_000).toISOString();
  return { fecha: d.slice(0, 10), hora: d.slice(11, 16) };
};
export const diaAR = (iso) => partesAR(iso).fecha;
export const cuando = (iso) => formatDateTime24(iso, { timeZone: TZ, dateOptions: { weekday: 'short', day: 'numeric', month: 'short' } });
export const hoyAR = () => partesAR(new Date().toISOString()).fecha;
export const sumarDias = (fecha, n) => {
  const d = new Date(`${fecha}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Textos sugeridos (sin IA paga): se completan con los datos de la obra. */
export const PLANTILLAS = [
  { id: 'avance', t: 'Avance de la semana', texto: '📸 Así avanza {obra} esta semana.\n\nSeguimos sumando metros y cumpliendo los plazos. Gracias a todo el equipo que lo hace posible 💪\n\n{empresa}' },
  { id: 'timelapse', t: 'Timelapse', texto: '⏱️ Meses de obra en segundos.\n\nMirá cómo creció {obra} desde el primer día. Registrado con cámara 24/7.\n\n¿Cuál es tu parte favorita? 👇' },
  { id: 'antes', t: 'Antes y después', texto: '⬅️ Antes / Después ➡️\n\n{obra}: del terreno a lo que ves hoy. Deslizá para ver el cambio.\n\n{empresa}' },
  { id: 'hito', t: 'Hito cumplido', texto: '✅ ¡Hito cumplido en {obra}!\n\nTerminamos una etapa clave del proyecto. Vamos por la siguiente 🏗️\n\n{empresa}' },
  { id: 'inversores', t: 'Para compradores', texto: '🏢 Tu futuro departamento en {obra}, día a día.\n\nSeguí la obra en vivo y mirá cómo avanza tu inversión.\n\nConsultas por mensaje directo 📩' },
  { id: 'equipo', t: 'Gracias al equipo', texto: '👷 Detrás de cada avance hay un gran equipo.\n\nGracias a todas las personas que todos los días hacen realidad {obra}.\n\n{empresa}' },
  { id: 'dia', t: 'Un día en la obra', texto: '🌅 De la mañana a la tarde: así fue un día en {obra}.\n\n{fecha}' },
];

export const HASHTAGS = ['obra', 'construccion', 'avancedeobra', 'arquitectura', 'timelapse', 'desarrollo', 'realestate', 'inversion', 'ingenieria', 'obraencurso'];

export const completar = (texto, datos) =>
  texto
    .replace(/\{obra\}/g, datos.obra || 'la obra')
    .replace(/\{empresa\}/g, datos.empresa || '')
    .replace(/\{fecha\}/g, new Date().toLocaleDateString('es-AR', { day: 'numeric', month: 'long', timeZone: TZ }))
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/** Qué tipos se pueden elegir y para qué redes vale cada uno. */
export const TIPOS = [
  { id: 'post', t: 'Post', d: 'Foto o carrusel en el feed', redes: ['instagram', 'facebook', 'linkedin'] },
  { id: 'reel', t: 'Reel / video', d: 'Un video (timelapse o pieza animada)', redes: ['instagram', 'facebook', 'linkedin'] },
  { id: 'historia', t: 'Historia', d: 'Dura 24 h · sólo Instagram', redes: ['instagram'] },
  { id: 'texto', t: 'Sólo texto', d: 'Sin foto ni video', redes: ['facebook', 'linkedin'] },
];

/** Tipo del backend según lo elegido y los archivos. */
export const tipoBackend = (tipo, media) => {
  if (tipo === 'historia') return 'historia';
  if (tipo === 'texto' || !media.length) return 'texto';
  if (tipo === 'reel' || media[0]?.tipo === 'video') return 'reel';
  return media.length > 1 ? 'carrusel' : 'imagen';
};
